import { zodResolver } from '@hookform/resolvers/zod';
import {
  AI_GROUP_MAX_PLAYERS,
  sessionCreateInput,
  sessionCredits,
  type SessionDto,
} from '@lila/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Field } from '../../../components/ui/Field';
import { useIdentity } from '../../../features/auth/useAuth';
import { useBalance } from '../../../features/billing/api';
import { useTraditions } from '../../../features/board/api';
import { errorKeyFor } from '../../../lib/errors';
import { isLocale } from '../../../lib/i18n';
import { trpcClient } from '../../../lib/trpc';

/** Spec 13.6: a facilitator creates a session, then runs it from the manage screen. */
export const Route = createFileRoute('/$lang/studio/sessions/new')({
  component: Page,
  /*
   * The home dashboard's board picker hands the chosen board here as a search param, so the
   * facilitator can set the board there and create with it in one move. Anything else is
   * dropped — an unknown id just falls back to the active default, which the schema allows.
   */
  validateSearch: (search: Record<string, unknown>): { boardId?: string } =>
    typeof search['boardId'] === 'string' ? { boardId: search['boardId'] } : {},
});

/** Spec 11.3: "Warns if credits are low." */
const LOW_CREDIT_THRESHOLD = 2;

type FormValues = z.input<typeof sessionCreateInput>;

function Page(): ReactNode {
  const { lang } = Route.useParams();
  const { boardId } = Route.useSearch();
  const { t } = useTranslation(['studio', 'common', 'billing']);
  const navigate = useNavigate();
  const balance = useBalance();
  const traditions = useTraditions();
  const { identity } = useIdentity();

  /*
   * The board this form creates with: the one carried in from the dashboard picker, else the
   * user's persisted preference (§1), else the active default the schema falls back to.
   */
  const initialBoardId = boardId ?? identity?.preferredBoardId ?? undefined;
  const pickedBoard = traditions.data?.find((tr) => tr.boardId === initialBoardId) ?? null;

  /*
   * The shared schema rather than hand-rolled `useState`, so the client refuses exactly what
   * the server refuses (CLAUDE.md hard rule 12) — `maxPlayers` between 1 and 7 is one
   * definition, not two that agree today.
   *
   * The three generics are load-bearing under `exactOptionalPropertyTypes`: a schema with
   * defaults has different input and output types, and collapsing them makes the resolver
   * unassignable. Same reasoning as `LoginForm`.
   */
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues, unknown, z.output<typeof sessionCreateInput>>({
    resolver: zodResolver(sessionCreateInput),
    /* §1: `facilitatorType` and `maxPlayers` are the two axes; the rest keep their schema
     * defaults. Rule set is no longer chosen — it follows the tradition's board. */
    defaultValues: {
      locale: isLocale(lang) ? lang : 'en',
      facilitatorType: 'human',
      maxPlayers: 7,
      title: '',
      /* Pinned from the picker or the saved preference; omitted → the active default board. */
      ...(initialBoardId === undefined ? {} : { boardId: initialBoardId }),
    },
  });

  /* §5.1: show the weighted credit cost live, before creation — AI groups cost more. */
  const facilitatorType = watch('facilitatorType') ?? 'human';
  const maxPlayers = watch('maxPlayers') || 1;
  const cost = sessionCredits(facilitatorType, maxPlayers);

  const create = useMutation({
    mutationFn: (values: z.output<typeof sessionCreateInput>): Promise<SessionDto> =>
      trpcClient.session.create.mutate(values),
    onSuccess: (session) => {
      /* Straight to the room: the next thing a facilitator does is share the code. */
      void navigate({ to: '/$lang/studio/sessions/$id/manage', params: { lang, id: session.id } });
    },
    retry: false,
  });

  /* `null` when unlimited — see the note in `features/billing/api.ts`. */
  const facilitated =
    balance.data === undefined
      ? undefined
      : balance.data.unlimited
        ? null
        : balance.data.credits.facilitated;

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6">
      <h1 className="text-xl font-semibold text-ink">{t('studio:create.title')}</h1>

      {/*
       * Before the invitations go out, not after. A session can be created with no credit —
       * consumption is at START (spec 20.2) — so this is a warning rather than a block, and
       * a facilitator can still set the room up and top up before starting it.
       */}
      {facilitated === 0 && (
        <Alert tone="error">
          {t('studio:dashboard.noCreditsTitle')} — {t('studio:dashboard.noCreditsBody')}
        </Alert>
      )}
      {facilitated !== undefined &&
        facilitated !== null &&
        facilitated > 0 &&
        facilitated <= LOW_CREDIT_THRESHOLD && (
          <Alert tone="info">{t('studio:dashboard.lowCreditsBody', { count: facilitated })}</Alert>
        )}

      {pickedBoard !== null && (
        <p className="text-sm text-ink-subtle">
          {t('studio:home.board.label')} · «{pickedBoard.displayName}»
        </p>
      )}

      <Card title={t('studio:create.title')}>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            /* `handleSubmit` returns a promise; a DOM handler must not hand one back. */
            void handleSubmit((values) => {
              create.mutate(values);
            })(event);
          }}
        >
          <Field
            label={t('studio:create.sessionTitle')}
            hint={t('studio:create.titleHint')}
            maxLength={120}
            error={errors.title?.message}
            /*
             * An untouched field holds `''`, and `sessionCreateInput.title` is
             * `.min(1).optional()` — optional means ABSENT, not empty, so the resolver
             * rejected the blank with "expected string to have >=1 characters" and made an
             * untitled session impossible. Spec 8.2 marks `title?`; normalising here keeps
             * one schema (hard rule 12) rather than a looser copy for the form.
             */
            {...register('title', {
              setValueAs: (value: string) => (value === '' ? undefined : value),
            })}
          />

          {/* §1 axis 1 — who guides. AI groups are capped smaller than human ones (§5.4). */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink">{t('studio:create.facilitatorType')}</span>
            <select
              className="rounded border border-line bg-surface px-3 py-2 text-ink"
              {...register('facilitatorType')}
            >
              <option value="human">{t('studio:create.facilitatorHuman')}</option>
              <option value="ai">{t('studio:create.facilitatorAi')}</option>
            </select>
          </label>

          {/* §1 axis 3 — a human facilitator may also play. Meaningless for an AI session. */}
          {facilitatorType === 'human' && (
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" {...register('hostPlays')} />
              {t('studio:create.hostPlays')}
            </label>
          )}

          <Field
            label={t('studio:create.maxPlayers')}
            hint={t('studio:create.maxPlayersHint')}
            type="number"
            min={1}
            /* Spec 7.4 allows 1–7; an AI group is capped smaller until real cost data (§5.4). */
            max={facilitatorType === 'ai' ? AI_GROUP_MAX_PLAYERS : 7}
            required
            error={errors.maxPlayers?.message}
            {...register('maxPlayers', { valueAsNumber: true })}
          />

          {/* §6.1: a free rehearsal — consumes no credit, excluded from analytics/results. */}
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" {...register('isPractice')} />
            {t('studio:create.isPractice')}
          </label>

          {/* §5.1: the weighted cost, shown before creation. */}
          <p className="text-sm text-ink-subtle">
            {t('studio:create.creditCost', { count: cost })}
          </p>

          {create.isError && <Alert tone="error">{t(errorKeyFor(create.error))}</Alert>}

          <Button type="submit" loading={create.isPending}>
            {create.isPending ? t('studio:create.creating') : t('studio:create.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
